"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import RichText from "@/components/atoms/rich-text/rich-text";
import { RenewalPaymentSummary } from "@/config/app-constant";
import { useRenewalPaymentSummaryStateContext } from "@/providers/renewal-payment-summary-auto-pay-state-provider";
import { RootState } from "@/store/root-reducer";

import AutoPaySectionButtonGroup from "./auto-pay-section-button-group";

type AutoPaySectionProps = {
  title?: string;
  description?: string;
  directDebitText?: string;
  ctaOne?: {
    ctaOneTitle?: string;
    ctaOneDescription?: string;
  };
  ctaTwo?: {
    ctaTwoTitle?: string;
    ctaTwoDescription?: string;
  };
  isVoucherImpact?: boolean;
};

export default function AutoPaySection({
  title = "",
  description = "",
  directDebitText = "",
  ctaOne = {},
  ctaTwo = {},
  isVoucherImpact = false,
}: Readonly<AutoPaySectionProps>) {
  const [selectedAnswer, setSelectedAnswer] = useState(0);
  const { setAutoPaySelected } = useRenewalPaymentSummaryStateContext();
  const buttonClick = (e: number) => {
    setSelectedAnswer(e);
    setAutoPaySelected(e === 0);
  };

  const { attributes } = useSelector((state: RootState) => state.quote);

  const { paymentDetails } = attributes;
  const isAutoPayEnabled =
    !!paymentDetails?.paymentOptions[0]?.cardPayment?.autoPayPayment
      ?.isAutoPayEnabled;

  const hasImpactOnAutoPay = useMemo(() => {
    return isVoucherImpact;
  }, [isVoucherImpact]);

  useEffect(() => {
    if (hasImpactOnAutoPay) {
      setAutoPaySelected(false);
    } else if (
      paymentDetails?.autoPaySelected !== undefined &&
      paymentDetails?.autoPaySelected !== null &&
      isAutoPayEnabled
    ) {
      setAutoPaySelected(paymentDetails?.autoPaySelected ?? null);
      setSelectedAnswer(paymentDetails.autoPaySelected ? 0 : 1);
    } else if (!isAutoPayEnabled) {
      setAutoPaySelected(false);
    } else {
      setAutoPaySelected(true);
      setSelectedAnswer(0);
    }
  }, [
    isAutoPayEnabled,
    paymentDetails,
    setAutoPaySelected,
    hasImpactOnAutoPay,
  ]);
  return (
    <div className="rounded-radius-xxl bg-surface-neutral-option-1">
      <div className="lg:px-spacing-4xl  lg:py-spacing-xxl md:px-spacing-xxl md:py-spacing-xxl px-spacing-m pb-spacing-l pt-spacing-m">
        {title && (
          <h1
            className="font-header-font md:text-[31px] text-[23px]
      md:leading-custom-size-xl leading-custom-size-compact text-text-title"
          >
            {title}
          </h1>
        )}

        {description && (
          <RichText
            className="text-[18px] text-text-body leading-[27px] [&_ol_li]:pt-[8px] md:mt-spacing-m mt-spacing-m"
            richText={description ?? ""}
          />
        )}
        {isAutoPayEnabled && !isVoucherImpact && (
          <div className="lg:mt-spacing-xl mt-spacing-l">
            <AutoPaySectionButtonGroup
              selectedAnswer={selectedAnswer}
              answerArray={[
                {
                  id: 0,
                  label: ctaOne.ctaOneTitle ?? "",
                  description: ctaOne.ctaOneDescription ?? "",
                },
                {
                  id: 1,
                  label: ctaTwo.ctaTwoTitle ?? "",
                  description: ctaTwo.ctaTwoDescription ?? "",
                },
              ]}
              type="policy-renewal"
              isDisable={false}
              setSelectedAnswer={buttonClick}
              location={RenewalPaymentSummary}
            />
          </div>
        )}
        {directDebitText && (
          <div className="lg:mt-spacing-xl mt-spacing-l">
            <RichText
              richText={directDebitText}
              className="font-body-font  text-[18px] leading-[27px] text-text-body grid gap-spacing-xs"
            />
          </div>
        )}
      </div>
    </div>
  );
}
